import { useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import { HELP_TEXT } from '../../components/claude-code/agentTelemetryHelp';
import { HelpLabel } from '../../components/claude-code/HelpTooltip';
import SkillProposalQueue from '../../components/claude-code/SkillProposalQueue';
import { useSkillProposals } from '../../apps/apis/claudeCodeDashboard/hooks';
import type {
  DashboardSession,
  SkillProposalSource,
  SkillProposalStatus,
} from '../../apps/apis/claudeCodeDashboard/types';
import { buildSkillProposalQueryParams } from './skillProposalAccessPolicy';

const STATUS_OPTIONS: Array<{ label: string; value: '' | SkillProposalStatus }> = [
  { label: 'All', value: '' },
  { label: 'Pending review', value: 'pending_review' },
  { label: 'Approved', value: 'approved' },
  { label: 'Revision requested', value: 'revision_requested' },
  { label: 'Converted to shared', value: 'converted_to_shared' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Delivered', value: 'delivered' },
];

const SOURCE_OPTIONS: Array<{ label: string; value: '' | SkillProposalSource }> = [
  { label: 'Employee local submissions', value: 'local_submission' },
  { label: 'Legacy telemetry batch', value: 'telemetry_batch' },
  { label: 'All sources', value: '' },
];

const AgentSkillProposalsPage = () => {
  const [status, setStatus] = useState<'' | SkillProposalStatus>('pending_review');
  const [proposalSource, setProposalSource] = useState<'' | SkillProposalSource>('local_submission');
  const [employeeId, setEmployeeId] = useState('');
  const [role, setRole] = useState('');

  return (
    <ClaudeCodeLoginGate>
      {(session) => (
        <Container fluid className="py-4">
          <div className="d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="mb-1">
                <HelpLabel description={HELP_TEXT.skillProposals}>
                  {session.isAdmin ? 'Agent Skill Proposals' : 'My Agent Skill Proposals'}
                </HelpLabel>
              </h2>
              <div className="text-muted small">
                Signed in as <strong>{session.email}</strong>
                {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
              </div>
            </div>
          </div>
          <ProposalBody
            session={session}
            status={status}
            setStatus={setStatus}
            proposalSource={proposalSource}
            setProposalSource={setProposalSource}
            employeeId={employeeId}
            setEmployeeId={setEmployeeId}
            role={role}
            setRole={setRole}
          />
        </Container>
      )}
    </ClaudeCodeLoginGate>
  );
};

interface ProposalBodyProps {
  session: DashboardSession;
  status: '' | SkillProposalStatus;
  setStatus: (_status: '' | SkillProposalStatus) => void;
  proposalSource: '' | SkillProposalSource;
  setProposalSource: (_proposalSource: '' | SkillProposalSource) => void;
  employeeId: string;
  setEmployeeId: (_employeeId: string) => void;
  role: string;
  setRole: (_role: string) => void;
}

const ProposalBody = ({
  session,
  status,
  setStatus,
  proposalSource,
  setProposalSource,
  employeeId,
  setEmployeeId,
  role,
  setRole,
}: ProposalBodyProps) => {
  const proposalParams = buildSkillProposalQueryParams(session, {
    status,
    proposalSource,
    employeeId,
    role,
    limit: 100,
  });
  const proposals = useSkillProposals(proposalParams);
  const sourceDescription =
    SOURCE_OPTIONS.find((option) => option.value === proposalSource)?.label ?? 'Employee local submissions';

  const refresh = () => void proposals.refetch();

  if (proposals.error) {
    return <Alert variant="danger">Failed to load skill proposals: {(proposals.error as Error).message}</Alert>;
  }

  return (
    <>
      {!session.isAdmin ? (
        <Alert variant="info">
          This page shows only your locally submitted personal skill proposals. Shared, role, and company-wide review
          remains admin-managed.
        </Alert>
      ) : null}
      {session.isAdmin ? (
        <Alert variant="info">
          The default queue is limited to employee-submitted local proposals. Legacy telemetry batch proposals are
          hidden unless you intentionally select that source.
        </Alert>
      ) : null}
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap align-items-end">
            <Form.Group style={{ minWidth: 180 }}>
              <Form.Label className="small text-muted">
                <HelpLabel description={HELP_TEXT.proposalStatus}>Status</HelpLabel>
              </Form.Label>
              <Form.Select
                value={status}
                onChange={(event) => setStatus(event.target.value as '' | SkillProposalStatus)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option value={option.value} key={option.value || 'all'}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {session.isAdmin ? (
              <>
                <Form.Group style={{ minWidth: 240 }}>
                  <Form.Label className="small text-muted">
                    <HelpLabel description={HELP_TEXT.proposalSource}>Source</HelpLabel>
                  </Form.Label>
                  <Form.Select
                    value={proposalSource}
                    onChange={(event) => setProposalSource(event.target.value as '' | SkillProposalSource)}
                  >
                    {SOURCE_OPTIONS.map((option) => (
                      <option value={option.value} key={option.value || 'all'}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group style={{ minWidth: 260 }}>
                  <Form.Label className="small text-muted">
                    <HelpLabel description="Filter by employee email when reviewing a specific teammate's submitted proposals.">
                      Employee
                    </HelpLabel>
                  </Form.Label>
                  <Form.Control
                    value={employeeId}
                    onChange={(event) => setEmployeeId(event.target.value)}
                    placeholder="toby@skin-seoul.com"
                  />
                </Form.Group>
                <Form.Group style={{ minWidth: 180 }}>
                  <Form.Label className="small text-muted">
                    <HelpLabel description={HELP_TEXT.roles}>Role</HelpLabel>
                  </Form.Label>
                  <Form.Control
                    value={role}
                    onChange={(event) => setRole(event.target.value)}
                    placeholder="development"
                  />
                </Form.Group>
              </>
            ) : null}
            <Button variant="outline-secondary" onClick={refresh} disabled={proposals.isFetching}>
              Refresh
            </Button>
          </div>
          <div className="text-muted small mt-2">
            Current source: {session.isAdmin ? sourceDescription : 'Employee local submissions'}. Review cards show only
            safe evidence summaries and skill drafts. Raw employee prompts, inputs, and outputs are not collected or
            shown here.
          </div>
        </Card.Body>
      </Card>

      {proposals.isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <SkillProposalQueue proposals={proposals.data?.proposals ?? []} />
      )}
    </>
  );
};

export default AgentSkillProposalsPage;
